/**
 * i18n 国际化配置 (懒加载)
 *
 * - 启动时只同步加载 zh (fallbackLocale 兜底必需) + 当前语言 (如果不是 zh)
 * - 其余语言只有用户真正切换过去时才动态加载
 * - zh/en 按命名空间拆到 locale/zh|en/*.json, 用 Vite glob 懒加载合并;
 *   (各 .json 只放自己的根 key, 不要跨文件重复同名根 key, 否则 Object.assign 会覆盖)
 * - ko/vi 保持单文件 (按 memory 只重点维护 zh/en)
 */

import { createI18n } from 'vue-i18n'
import { getLocale, setLocale, type SupportedLocale } from '@/utils/locale'
import { SUPPORTED_LOCALES } from '@/types/locale'

type LocaleMessages = Record<string, unknown>

// zh/en 每个命名空间文件对应一个懒加载函数, 只有真正用到该语言时才会触发动态 import
const zhModules = import.meta.glob<LocaleMessages>('./locale/zh/*.json', { import: 'default' })
const enModules = import.meta.glob<LocaleMessages>('./locale/en/*.json', { import: 'default' })

async function buildSplitLocale(modules: Record<string, () => Promise<LocaleMessages>>): Promise<LocaleMessages> {
  const out: LocaleMessages = {}
  const contents = await Promise.all(Object.values(modules).map(loader => loader()))
  contents.forEach(content => Object.assign(out, content))
  return out
}

async function loadMessages(locale: SupportedLocale): Promise<LocaleMessages> {
  switch (locale) {
    case 'zh':
      return buildSplitLocale(zhModules)
    case 'en':
      return buildSplitLocale(enModules)
    case 'ko':
      return (await import('./locale/ko.json')).default
    case 'vi':
      return (await import('./locale/vi.json')).default
  }
}

// 创建 i18n 实例 (messages 先留空, initI18n() 里异步填充当前语言;
// main.ts 会先 await initI18n() 再挂载 App, 所以不存在"未翻译文案闪一下"的问题)
const i18n = createI18n({
  legacy: false, // 使用 Composition API 模式
  locale: getLocale(), // 从工具函数获取语言
  fallbackLocale: 'zh',
  messages: {},
})

// 导出类型化的 global 对象
export const i18nGlobal = i18n.global

/**
 * 已加载的语言集合
 */
const loadedLanguages = new Set<SupportedLocale>()

/**
 * 动态加载语言包
 *
 * @param locale - 语言代码
 * @returns Promise<void>
 */
export async function loadLanguageAsync(locale: SupportedLocale): Promise<void> {
  // 如果语言已加载,直接返回
  if (loadedLanguages.has(locale)) {
    return
  }

  // 守卫: 未在 SUPPORTED_LOCALES 白名单内的语言不允许加载
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
    throw new Error(`UNSUPPORTED_LOCALE:${locale}`)
  }

  const messages = await loadMessages(locale)
  i18n.global.setLocaleMessage(locale, messages)
  loadedLanguages.add(locale)
}

/**
 * 切换语言（统一入口,支持懒加载）
 *
 * @param locale - 目标语言
 * @returns Promise<void>
 */
export async function changeLocale(locale: SupportedLocale): Promise<void> {
  // 守卫: 不支持的语言直接抛错, caller 应在调用前用 isSupportedLocale 预检
  if (!(SUPPORTED_LOCALES as readonly string[]).includes(locale)) {
    throw new Error(`UNSUPPORTED_LOCALE:${locale}`)
  }

  // 如果是当前语言,无需切换
  const currentLocale = i18n.global.locale.value
  if (currentLocale === locale) {
    return
  }

  // 先加载语言包(如果未加载)
  await loadLanguageAsync(locale)

  // 切换语言
  // 在 Composition API 模式下,locale 是一个 WritableComputedRef
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  ;(i18n.global.locale as any).value = locale

  // 保存到 localStorage
  setLocale(locale)

  // 设置 HTML lang 属性
  document.documentElement.lang = locale
}

/**
 * 预加载语言包(可选优化, 例如登录后在空闲时预取常用的几种语言)
 *
 * @param locales - 要预加载的语言列表
 */
export async function preloadLanguages(locales: SupportedLocale[]): Promise<void> {
  const promises = locales.map(locale => loadLanguageAsync(locale).catch(() => {}))
  await Promise.all(promises)
}

/**
 * 初始化 i18n (确保当前语言 + zh 兜底已加载)
 */
export async function initI18n(): Promise<void> {
  const currentLocale = getLocale()

  // zh 始终加载: 一是常见默认语言, 二是 fallbackLocale 兜底缺失 key 需要它
  await loadLanguageAsync('zh')

  if (currentLocale !== 'zh') {
    await loadLanguageAsync(currentLocale)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    ;(i18n.global.locale as any).value = currentLocale
  }
}

export default i18n
